"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Building2,
  Calculator,
  ChevronDown,
  ChevronsUpDown,
  CreditCard,
  FileText,
  Home,
  LogOut,
  PieChart,
  Settings,
  TrendingUp,
  UserCircle,
  Users,
  Briefcase,
  ClipboardList,
  Shield,
  Bell,
  HelpCircle,
  Target
} from "lucide-react";

const sidebarVariants = {
  open: {
    width: "16rem"
  },
  closed: {
    width: "4rem"
  }
};

const contentVariants = {
  open: { display: "block", opacity: 1 },
  closed: { display: "block", opacity: 1 }
};

const variants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      x: { stiffness: 1000, velocity: -100 }
    }
  },
  closed: {
    x: -20,
    opacity: 0,
    transition: {
      x: { stiffness: 100 }
    }
  }
};

const transitionProps = {
  type: "tween" as const,
  ease: "easeOut",
  duration: 0.2,
  staggerChildren: 0.1
};

const staggerVariants = {
  open: {
    transition: { staggerChildren: 0.03, delayChildren: 0.02 }
  }
};

interface MenuItem {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  badge?: string;
  subItems?: {
    id: string;
    title: string;
    href: string;
  }[];
}

interface MenuSection {
  title?: string;
  items: MenuItem[];
}

const menuSections: MenuSection[] = [
  {
    items: [
      {
        id: "dashboard",
        title: "Dashboard",
        icon: Home,
        href: "/dashboard"
      },
      {
        id: "pipeline",
        title: "Loan Pipeline",
        icon: TrendingUp,
        href: "/pipeline",
        subItems: [
          { id: "active-loans", title: "Active Loans", href: "/pipeline/active" },
          { id: "pending-approval", title: "Pending Approval", href: "/pipeline/pending" },
          { id: "closed-loans", title: "Closed Loans", href: "/pipeline/closed" }
        ]
      }
    ]
  },
  {
    title: "Loan Management",
    items: [
      {
        id: "applications",
        title: "Applications",
        icon: FileText,
        href: "/applications",
        badge: "12",
        subItems: [
          { id: "new-applications", title: "New Applications", href: "/applications/new" },
          { id: "in-review", title: "In Review", href: "/applications/review" },
          { id: "approved", title: "Approved", href: "/applications/approved" },
          { id: "denied", title: "Denied", href: "/applications/denied" }
        ]
      },
      {
        id: "underwriting",
        title: "Underwriting",
        icon: ClipboardList,
        href: "/underwriting",
        subItems: [
          { id: "income-verification", title: "Income Verification", href: "/underwriting/income" },
          { id: "credit-analysis", title: "Credit Analysis", href: "/underwriting/credit" },
          { id: "property-appraisal", title: "Property Appraisal", href: "/underwriting/appraisal" }
        ]
      },
      {
        id: "compliance",
        title: "Compliance",
        icon: Shield,
        href: "/compliance",
        subItems: [
          { id: "audit-trail", title: "Audit Trail", href: "/compliance/audit" },
          { id: "regulatory-reports", title: "Regulatory Reports", href: "/compliance/reports" },
          { id: "risk-assessment", title: "Risk Assessment", href: "/compliance/risk" }
        ]
      }
    ]
  },
  {
    title: "Customer Management",
    items: [
      {
        id: "borrowers",
        title: "Borrowers",
        icon: Users,
        href: "/borrowers",
        subItems: [
          { id: "active-borrowers", title: "Active Borrowers", href: "/borrowers/active" },
          { id: "prospect-borrowers", title: "Prospects", href: "/borrowers/prospects" },
          { id: "borrower-documents", title: "Documents", href: "/borrowers/documents" }
        ]
      },
      {
        id: "referral-partners",
        title: "Referral Partners",
        icon: Briefcase,
        href: "/partners",
        subItems: [
          { id: "real-estate-agents", title: "Real Estate Agents", href: "/partners/agents" },
          { id: "builders", title: "Builders", href: "/partners/builders" },
          { id: "financial-advisors", title: "Financial Advisors", href: "/partners/advisors" }
        ]
      }
    ]
  },
  {
    title: "Financial Tools",
    items: [
      {
        id: "pricing",
        title: "Pricing Engine",
        icon: Calculator,
        href: "/pricing",
        subItems: [
          { id: "rate-sheets", title: "Rate Sheets", href: "/pricing/rates" },
          { id: "loan-calculator", title: "Loan Calculator", href: "/pricing/calculator" },
          { id: "pricing-exceptions", title: "Pricing Exceptions", href: "/pricing/exceptions" }
        ]
      },
      {
        id: "payments",
        title: "Payments",
        icon: CreditCard,
        href: "/payments",
        subItems: [
          { id: "payment-processing", title: "Payment Processing", href: "/payments/processing" },
          { id: "escrow-management", title: "Escrow Management", href: "/payments/escrow" },
          { id: "payment-history", title: "Payment History", href: "/payments/history" }
        ]
      }
    ]
  },
  {
    title: "Analytics & Reports",
    items: [
      {
        id: "analytics",
        title: "Analytics",
        icon: PieChart,
        href: "/analytics",
        subItems: [
          { id: "loan-performance", title: "Loan Performance", href: "/analytics/performance" },
          { id: "origination-metrics", title: "Origination Metrics", href: "/analytics/origination" },
          { id: "profitability", title: "Profitability", href: "/analytics/profitability" }
        ]
      },
      {
        id: "reports",
        title: "Reports",
        icon: FileText,
        href: "/reports",
        subItems: [
          { id: "monthly-reports", title: "Monthly Reports", href: "/reports/monthly" },
          { id: "regulatory-reports", title: "Regulatory Reports", href: "/reports/regulatory" },
          { id: "custom-reports", title: "Custom Reports", href: "/reports/custom" }
        ]
      }
    ]
  }
];

export function MortgageSidebar() {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const [activeItem, setActiveItem] = React.useState("dashboard");

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (itemId: string, hasSubItems: boolean) => {
    setActiveItem(itemId);
    if (hasSubItems) {
      toggleExpanded(itemId);
    }
  };

  return (
    <motion.div
      className="fixed left-0 z-40 h-full shrink-0 border-r bg-background"
      initial={isCollapsed ? "closed" : "open"}
      animate={isCollapsed ? "closed" : "open"}
      variants={sidebarVariants}
      transition={transitionProps}
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
    >
      <motion.div
        className="relative z-40 flex h-full shrink-0 flex-col bg-background transition-all"
        variants={contentVariants}
      >
        <motion.ul variants={staggerVariants} className="flex h-full flex-col">
          <div className="flex grow flex-col">
            {/* Header */}
            <div className="flex h-16 w-full shrink-0 border-b p-3">
              <div className="flex w-full items-center">
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className="w-full" asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex w-fit items-center gap-2 px-2"
                    >
                      <Avatar className="size-6 rounded">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          MB
                        </AvatarFallback>
                      </Avatar>
                      <motion.li
                        variants={variants}
                        className="flex w-fit items-center gap-2"
                      >
                        {!isCollapsed && (
                          <>
                            <div className="flex flex-col items-start">
                              <p className="text-sm font-semibold">MortgageBank</p>
                              <p className="text-xs text-muted-foreground">Enterprise</p>
                            </div>
                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
                          </>
                        )}
                      </motion.li>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Company Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Manage Users
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Switch Organization
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex h-full w-full flex-col">
              <div className="flex grow flex-col">
                <ScrollArea className="h-full grow p-3">
                  <div className="flex w-full flex-col gap-3">
                    {menuSections.map((section, sectionIndex) => (
                      <div key={sectionIndex} className="space-y-2">
                        {section.title && !isCollapsed && (
                          <motion.div
                            variants={variants}
                            className="px-2 py-1"
                          >
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              {section.title}
                            </h3>
                          </motion.div>
                        )}
                        <div className="space-y-1">
                          {section.items.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeItem === item.id;
                            const isExpanded = expandedItems.includes(item.id);
                            const hasSubItems = item.subItems && item.subItems.length > 0;

                            return (
                              <div key={item.id}>
                                <button
                                  onClick={() => handleItemClick(item.id, hasSubItems || false)}
                                  className={cn(
                                    "flex h-10 w-full flex-row items-center rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-muted",
                                    isActive && "bg-primary/10 text-primary hover:bg-primary/15"
                                  )}
                                >
                                  <Icon className="h-4 w-4 shrink-0" />
                                  <motion.div
                                    variants={variants}
                                    className="flex w-full items-center justify-between"
                                  >
                                    {!isCollapsed && (
                                      <>
                                        <span className="ml-3 truncate">{item.title}</span>
                                        <div className="flex items-center gap-2">
                                          {item.badge && (
                                            <Badge
                                              variant="secondary"
                                              className="h-5 px-1.5 text-xs"
                                            >
                                              {item.badge}
                                            </Badge>
                                          )}
                                          {hasSubItems && (
                                            <ChevronDown
                                              className={cn(
                                                "h-4 w-4 transition-transform",
                                                isExpanded && "rotate-180"
                                              )}
                                            />
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </motion.div>
                                </button>

                                {/* Sub Items */}
                                {hasSubItems && isExpanded && !isCollapsed && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="ml-7 mt-1 space-y-1 border-l border-border pl-3"
                                  >
                                    {item.subItems?.map((subItem) => (
                                      <button
                                        key={subItem.id}
                                        onClick={() => setActiveItem(subItem.id)}
                                        className={cn(
                                          "flex h-8 w-full items-center rounded-md px-2 text-sm transition-colors hover:bg-muted",
                                          activeItem === subItem.id && "bg-muted text-primary"
                                        )}
                                      >
                                        {subItem.title}
                                      </button>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {sectionIndex < menuSections.length - 1 && (
                          <Separator className="my-2" />
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Footer */}
              <div className="flex flex-col border-t p-3">
                <button className="flex h-10 w-full flex-row items-center rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-muted">
                  <Settings className="h-4 w-4 shrink-0" />
                  <motion.div variants={variants}>
                    {!isCollapsed && (
                      <span className="ml-3">Settings</span>
                    )}
                  </motion.div>
                </button>

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className="w-full">
                    <div className="flex h-10 w-full flex-row items-center gap-3 rounded-lg px-3 py-2 transition-all hover:bg-muted">
                      <Avatar className="size-6">
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          JD
                        </AvatarFallback>
                      </Avatar>
                      <motion.div
                        variants={variants}
                        className="flex w-full items-center justify-between"
                      >
                        {!isCollapsed && (
                          <>
                            <div className="flex flex-col items-start">
                              <span className="text-sm font-medium">John Doe</span>
                              <span className="text-xs text-muted-foreground">Loan Officer</span>
                            </div>
                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />
                          </>
                        )}
                      </motion.div>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent sideOffset={5} className="w-56">
                    <div className="flex flex-row items-center gap-3 p-3">
                      <Avatar className="size-8">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          JD
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col text-left">
                        <span className="text-sm font-medium">John Doe</span>
                        <span className="text-xs text-muted-foreground">john.doe@mortgagebank.com</span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="flex items-center gap-2">
                      <UserCircle className="h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Notifications
                    </DropdownMenuItem>
                    <DropdownMenuItem className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4" />
                      Help & Support
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="flex items-center gap-2 text-red-600">
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </motion.ul>
      </motion.div>
    </motion.div>
  );
}

function MortgageSidebarDemo() {
  return (
    <div className="flex h-screen w-screen flex-row bg-background">
      <MortgageSidebar />
      <main className="flex h-screen grow flex-col overflow-auto pl-16">
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Mortgage Banking Dashboard</h1>
            <p className="text-muted-foreground mt-2">Hover over the sidebar to expand and explore menu options</p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MortgageSidebarDemo;